import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { projectsAPI } from '../services/api';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';

const CalendarPage = () => {
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await projectsAPI.getAll();
      const projects = response?.data?.data || response?.data || [];
      setProjects(projects.filter(p => p.start_date || p.end_date));
    } catch (error) {
      console.error('Error al cargar proyectos:', error);
      toast.error('Error al cargar proyectos');
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    return { daysInMonth, startingDay };
  };

  const formatMonth = (date) => {
    return date.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
  };

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const getProjectsForDate = (day) => {
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    return projects.filter(project => {
      const start = project.start_date;
      const end = project.end_date;
      
      if (start === dateStr || end === dateStr) return true;
      if (start && end && dateStr >= start && dateStr <= end) return true;
      
      return false;
    });
  };

  const { daysInMonth, startingDay } = getDaysInMonth(currentDate);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const emptyDays = Array.from({ length: startingDay }, (_, i) => i);

  const getStatusColor = (status) => {
    switch (status) {
      case 'activo': return 'bg-[#C05621]';
      case 'completado': return 'bg-[#81A183]';
      case 'pausado': return 'bg-amber-500';
      default: return 'bg-gray-400';
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-96 rounded-xl" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="calendar-page">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#2D2A26]">Calendario de Obras</h1>
          <p className="text-[#5C554B]">Vista general de fechas de proyectos</p>
        </div>

        <Card className="card-dashboard">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <Button variant="ghost" size="icon" onClick={prevMonth}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <CardTitle className="text-xl capitalize">{formatMonth(currentDate)}</CardTitle>
            <Button variant="ghost" size="icon" onClick={nextMonth}>
              <ChevronRight className="w-5 h-5" />
            </Button>
          </CardHeader>
          <CardContent>
            {/* Days of week header */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map((day) => (
                <div key={day} className="text-center text-sm font-medium text-[#5C554B] py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1">
              {/* Empty days */}
              {emptyDays.map((_, index) => (
                <div key={`empty-${index}`} className="aspect-square p-1" />
              ))}

              {/* Days */}
              {days.map((day) => {
                const projectsOnDay = getProjectsForDate(day);
                const isToday = 
                  day === new Date().getDate() && 
                  currentDate.getMonth() === new Date().getMonth() &&
                  currentDate.getFullYear() === new Date().getFullYear();

                return (
                  <div
                    key={day}
                    className={`
                      aspect-square p-1 rounded-lg border transition-colors
                      ${isToday ? 'border-[#C05621] bg-[#C05621]/5' : 'border-transparent hover:bg-[#EBE5DA]'}
                      ${projectsOnDay.length > 0 ? 'cursor-pointer' : ''}
                    `}
                  >
                    <div className="h-full flex flex-col">
                      <span className={`
                        text-sm font-medium
                        ${isToday ? 'text-[#C05621]' : 'text-[#2D2A26]'}
                      `}>
                        {day}
                      </span>
                      <div className="flex-1 overflow-hidden">
                        {projectsOnDay.slice(0, 2).map((project, idx) => (
                          <div
                            key={project.id}
                            className={`
                              text-xs px-1 py-0.5 rounded truncate mb-0.5 text-white
                              ${getStatusColor(project.status)}
                            `}
                            title={project.name}
                          >
                            {project.name}
                          </div>
                        ))}
                        {projectsOnDay.length > 2 && (
                          <span className="text-xs text-[#5C554B]">
                            +{projectsOnDay.length - 2} más
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Legend */}
        <Card className="card-dashboard">
          <CardContent className="pt-6">
            <h3 className="font-semibold text-[#2D2A26] mb-4">Leyenda</h3>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-[#C05621]" />
                <span className="text-sm text-[#5C554B]">Activo</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-[#81A183]" />
                <span className="text-sm text-[#5C554B]">Completado</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-amber-500" />
                <span className="text-sm text-[#5C554B]">Pausado</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Project List */}
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle>Proyectos con fechas programadas</CardTitle>
          </CardHeader>
          <CardContent>
            {projects.length === 0 ? (
              <div className="text-center py-8">
                <CalendarIcon className="w-12 h-12 text-[#E2E8F0] mx-auto mb-3" />
                <p className="text-[#5C554B]">No hay proyectos con fechas programadas</p>
              </div>
            ) : (
              <div className="space-y-3">
                {projects.map((project) => (
                  <div 
                    key={project.id}
                    className="flex items-center justify-between p-3 rounded-lg border border-[#E2E8F0] hover:bg-[#FDFBF7] transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${getStatusColor(project.status)}`} />
                      <div>
                        <p className="font-medium text-[#2D2A26]">{project.name}</p>
                        <p className="text-sm text-[#5C554B]">
                          {project.start_date || 'Sin inicio'} - {project.end_date || 'Sin fin'}
                        </p>
                      </div>
                    </div>
                    <Badge className={`
                      ${project.status === 'activo' ? 'bg-[#C05621]/10 text-[#C05621]' : 
                        project.status === 'completado' ? 'bg-green-100 text-green-800' : 
                        'bg-amber-100 text-amber-800'}
                    `}>
                      {project.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default CalendarPage;
